import { useTranslate } from "ra-core";

import type { SaleSplitOutcome } from "../corretaje/corretajeSale";
import { formatMxn } from "../quoter/PlanSchedule";

/**
 * Almikia Epic EC (TASK-032): the computed split of a corretaje sale with its
 * case number and the amount per party. It shows the parties of THIS sale
 * only; it never ranks or compares advisors. `isSnapshot` marks the amounts
 * frozen by the Direccion VoBo.
 */
export const CorretajeSplitPanel = ({
  outcome,
  isSnapshot,
}: {
  outcome: SaleSplitOutcome | null;
  isSnapshot: boolean;
}) => {
  const translate = useTranslate();

  if (!outcome) {
    return (
      <p
        data-testid="corretaje-split"
        className="text-sm text-muted-foreground"
      >
        {translate("crm.corretaje.sale.split_unavailable")}
      </p>
    );
  }
  if (!outcome.ok) {
    return (
      <p
        data-testid="corretaje-split"
        className="text-sm text-muted-foreground"
      >
        {outcome.message}
      </p>
    );
  }

  const { split } = outcome;
  const rows = [
    { key: "colega", amount: split.colega_amount },
    { key: "vendedora", amount: split.vendedora_amount },
    { key: "opcionadora", amount: split.opcionadora_amount },
    { key: "almikia", amount: split.almikia_amount },
  ].filter((row) => row.key === "almikia" || row.amount > 0);

  return (
    <div data-testid="corretaje-split" className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <h4 className="font-semibold">
          {translate("crm.corretaje.sale.split_title")}
        </h4>
        <span data-testid="split-case" className="text-base font-semibold">
          {translate("crm.corretaje.sale.case", { code: split.case_code })}
        </span>
      </div>
      <dl className="grid gap-2 text-base sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.key} data-testid={`split-${row.key}`}>
            <dt className="text-sm text-muted-foreground">
              {translate(`crm.corretaje.sale.parties.${row.key}`)}
            </dt>
            <dd className="tabular-nums">{formatMxn(row.amount)}</dd>
          </div>
        ))}
      </dl>
      {isSnapshot ? (
        <p className="text-sm text-muted-foreground">
          {translate("crm.corretaje.sale.split_snapshot")}
        </p>
      ) : null}
    </div>
  );
};
